import React from "react";
import { useReducedMotion } from "framer-motion";

/**
 * CountUp — animates a number from 0 to `value` when scrolled into view.
 * Fires once. Respects prefers-reduced-motion.
 *
 * Props:
 *  - value: number (target)
 *  - duration: ms (default 900)
 *  - decimals: how many decimals to keep (auto if value has a decimal)
 *  - prefix / suffix: strings rendered around the number
 */
export default function CountUp({
  value,
  duration = 900,
  decimals,
  prefix = "",
  suffix = "",
  className = "",
  ...rest
}) {
  const prefersReducedMotion = useReducedMotion();
  const [display, setDisplay] = React.useState(prefersReducedMotion ? value : 0);
  const [started, setStarted] = React.useState(false);
  const ref = React.useRef(null);

  const dec = decimals ?? (Number.isInteger(value) ? 0 : 1);

  React.useEffect(() => {
    if (prefersReducedMotion) {
      setDisplay(value);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started) {
            setStarted(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.3, rootMargin: "-40px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [prefersReducedMotion, started, value]);

  React.useEffect(() => {
    if (!started || prefersReducedMotion) return;
    let raf = 0;
    const startT = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - startT) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const next = value * eased;
      setDisplay(next);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, prefersReducedMotion, value, duration]);

  const formatted = dec === 0 ? Math.round(display).toString() : display.toFixed(dec);

  return (
    <span ref={ref} className={className} {...rest}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}
